import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { useBranding } from "@/lib/branding";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Activity, AlertTriangle, MapPin, ShieldAlert, ClipboardList, Hexagon, Building2,
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

type SearchParams = { site?: string };

export const Route = createFileRoute("/_authenticated/portal")({
  validateSearch: (s: Record<string, unknown>): SearchParams => ({
    site: typeof s.site === "string" ? s.site : undefined,
  }),
  component: ClientPortalPage,
  head: () => ({ meta: [{ title: "Client Portal — MissionReady" }] }),
});

type Site = { id: string; name: string };
type ActivityItem = {
  id: number | string;
  event_type: string;
  severity: string;
  title: string;
  body: string | null;
  site_id: string | null;
  created_at: string;
};
type Incident = {
  id: string;
  reference_number: string | null;
  title: string | null;
  category: string;
  severity: string;
  status: string;
  site_id: string | null;
  created_at: string;
};
type TourSummary = {
  tour_name: string;
  site_id: string | null;
  site_name: string | null;
  total: number;
  completed: number;
  missed: number;
  last_run_at: string | null;
};
type GeoEvent = {
  id: string;
  event_type: string;
  geofence_id: string;
  patrol_id: string | null;
  occurred_at: string | null;
  created_at: string;
};

const sevTone = (s: string) =>
  s === "critical" ? "text-destructive" :
  s === "warning" || s === "high" ? "text-warning" :
  s === "success" ? "text-success" :
  "text-muted-foreground";

function ClientPortalPage() {
  const { currentOrg } = useAuth();
  const branding = useBranding();
  const [sites, setSites] = useState<Site[]>([]);
  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [tours, setTours] = useState<TourSummary[]>([]);
  const [geo, setGeo] = useState<GeoEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const siteFilter = Route.useSearch().site ?? "all";
  const navigate = useNavigate({ from: Route.fullPath });

  const siteMap = useMemo(() => Object.fromEntries(sites.map((s) => [s.id, s.name])), [sites]);

  const matchSite = (id: string | null | undefined) => siteFilter === "all" || id === siteFilter;
  const filteredActivity = useMemo(() => activity.filter((a) => matchSite(a.site_id)), [activity, siteFilter]);
  const filteredIncidents = useMemo(() => incidents.filter((i) => matchSite(i.site_id)), [incidents, siteFilter]);
  const filteredTours = useMemo(() => tours.filter((t) => matchSite(t.site_id)), [tours, siteFilter]);


  useEffect(() => {
    if (!currentOrg) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data: siteRows } = await supabase.rpc("client_assigned_sites", { _org: currentOrg.id });
      if (cancelled) return;
      setSites((siteRows ?? []) as Site[]);

      const [{ data: af }, { data: inc }, { data: tr }, { data: ge }] = await Promise.all([
        supabase.from("activity_feed")
          .select("id,event_type,severity,title,body,site_id,created_at")
          .eq("organization_id", currentOrg.id)
          .order("created_at", { ascending: false })
          .limit(50),
        supabase.from("incidents")
          .select("id,reference_number,title,category,severity,status,site_id,created_at")
          .eq("organization_id", currentOrg.id)
          .order("created_at", { ascending: false })
          .limit(50),
        supabase.from("tour_runs")
          .select("id,status,started_at,ended_at,completion_state,missed_count,tour_id,guard_tours(name,site_id)")
          .eq("organization_id", currentOrg.id)
          .order("started_at", { ascending: false })
          .limit(200),
        supabase.from("geofence_events")
          .select("id,event_type,geofence_id,patrol_id,occurred_at,created_at")
          .eq("organization_id", currentOrg.id)
          .in("event_type", ["breach", "exit"])
          .order("created_at", { ascending: false })
          .limit(50),
      ]);

      if (cancelled) return;
      setActivity((af ?? []) as any);
      setIncidents((inc ?? []) as any);
      setGeo((ge ?? []) as any);

      // guard_tours.site_id has no declared foreign key to sites, so site names
      // come from a separate org-scoped read rather than a nested embed.
      const tourSiteIds = [
        ...new Set(((tr ?? []) as any[]).map((r) => r.guard_tours?.site_id).filter(Boolean) as string[]),
      ];
      const siteNameById = new Map<string, string>();
      if (tourSiteIds.length > 0) {
        const { data: siteRows } = await supabase
          .from("sites")
          .select("id,name")
          .eq("organization_id", currentOrg.id)
          .in("id", tourSiteIds);
        for (const s of (siteRows ?? []) as { id: string; name: string }[]) siteNameById.set(s.id, s.name);
      }

      // Roll up tour runs → per-tour summary
      const map = new Map<string, TourSummary>();
      for (const r of (tr ?? []) as any[]) {
        const name = r.guard_tours?.name ?? "Patrol";
        const siteName = (r.guard_tours?.site_id ? siteNameById.get(r.guard_tours.site_id) : null) ?? null;
        const siteId = r.guard_tours?.site_id ?? null;
        const key = `${r.tour_id}`;
        const cur = map.get(key) ?? { tour_name: name, site_id: siteId, site_name: siteName, total: 0, completed: 0, missed: 0, last_run_at: null };
        cur.total += 1;
        if (r.completion_state === "completed") cur.completed += 1;
        if ((r.missed_count ?? 0) > 0 || r.completion_state === "completed_with_missed" || r.completion_state === "incomplete" || r.status === "abandoned") cur.missed += 1;
        const t = r.ended_at || r.started_at;
        if (t && (!cur.last_run_at || t > cur.last_run_at)) cur.last_run_at = t;
        map.set(key, cur);
      }
      setTours(Array.from(map.values()).sort((a, b) => (b.last_run_at ?? "").localeCompare(a.last_run_at ?? "")));
      setLoading(false);
    })();

    const ch = supabase
      .channel(`client-portal:${currentOrg.id}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "activity_feed", filter: `organization_id=eq.${currentOrg.id}` },
        (p) => setActivity((prev) => [p.new as any, ...prev].slice(0, 50)))
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "geofence_events", filter: `organization_id=eq.${currentOrg.id}` },
        (p) => {
          const row = p.new as any;
          if (row.event_type === "breach" || row.event_type === "exit") {
            setGeo((prev) => [row, ...prev].slice(0, 50));
          }
        })
      .subscribe();

    return () => { cancelled = true; supabase.removeChannel(ch); };
  }, [currentOrg?.id]);

  if (!currentOrg) {
    return <div className="p-8 text-sm text-muted-foreground">Select an organization.</div>;
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 md:px-8">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5 shadow-elegant">
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-xl border border-border bg-background">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt={currentOrg.name} className="h-full w-full object-contain" />
            ) : (
              <Building2 className="h-6 w-6 text-muted-foreground" />
            )}
          </div>
          <div>
            <h1 className="font-display text-2xl font-semibold tracking-tight">{currentOrg.name}</h1>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Client portal · read-only view of {sites.length === 0 ? "your sites" : sites.map((s) => s.name).join(", ")}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {sites.length > 1 && (
            <Select value={siteFilter} onValueChange={(v) => navigate({ search: { site: v } })}>
              <SelectTrigger aria-label="All sites" className="h-9 w-44"><SelectValue placeholder="All sites" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sites</SelectItem>
                {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Badge variant="outline" className="border-primary/40 text-primary">
            <MapPin className="mr-1 h-3 w-3" />
            {sites.length} site{sites.length === 1 ? "" : "s"}
          </Badge>
        </div>
      </div>


      {sites.length === 0 && !loading && (
        <div className="mb-6 rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">
          Your account isn't linked to any sites yet. Ask your organization admin to grant site access.
        </div>
      )}


      <Tabs defaultValue="activity">
        <TabsList className="mb-4">
          <TabsTrigger value="activity"><Activity className="mr-1.5 h-4 w-4" />Activity</TabsTrigger>
          <TabsTrigger value="incidents"><AlertTriangle className="mr-1.5 h-4 w-4" />Incidents</TabsTrigger>
          <TabsTrigger value="tours"><ClipboardList className="mr-1.5 h-4 w-4" />Patrol Tours</TabsTrigger>
          <TabsTrigger value="geofence"><Hexagon className="mr-1.5 h-4 w-4" />Breaches</TabsTrigger>
        </TabsList>

        <TabsContent value="activity">
          <div className="rounded-xl border border-border bg-card">
            <div className="flex items-center justify-between border-b border-border p-3">
              <h2 className="text-sm font-semibold">Live activity feed</h2>
              <span className="inline-flex items-center gap-1.5 text-[10px] text-success">
                <span className="h-1.5 w-1.5 rounded-full bg-success animate-pulse" /> live
              </span>
            </div>
            <div className="max-h-[560px] divide-y divide-border overflow-y-auto">
              {filteredActivity.length === 0 ? (
                <div className="p-6 text-center text-xs text-muted-foreground">No activity yet.</div>
              ) : filteredActivity.map((a) => (
                <div key={a.id} className="flex gap-3 p-3">
                  <Activity className={`mt-0.5 h-4 w-4 shrink-0 ${sevTone(a.severity)}`} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{a.title}</div>
                    {a.body && <div className="truncate text-xs text-muted-foreground">{a.body}</div>}
                    <div className="mt-0.5 text-[10px] text-muted-foreground">
                      {a.site_id ? (siteMap[a.site_id] ?? "—") : "—"} · {new Date(a.created_at).toLocaleString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="incidents">
          <div className="rounded-xl border border-border bg-card">
            <div className="border-b border-border p-3">
              <h2 className="text-sm font-semibold">Incident reports</h2>
            </div>
            <div className="divide-y divide-border">
              {filteredIncidents.length === 0 ? (
                <div className="p-6 text-center text-xs text-muted-foreground">No incidents to display.</div>
              ) : filteredIncidents.map((i) => (
                <div key={i.id} className="flex flex-wrap items-center gap-3 p-3">
                  <AlertTriangle className={`h-4 w-4 ${sevTone(i.severity)}`} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">
                      {i.reference_number ? <span className="text-muted-foreground">{i.reference_number} · </span> : null}
                      {i.title ?? i.category}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {i.site_id ? (siteMap[i.site_id] ?? "—") : "—"} · {new Date(i.created_at).toLocaleString()}
                    </div>
                  </div>
                  <Badge variant="outline">{i.severity}</Badge>
                  <Badge variant="secondary">{i.status}</Badge>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="tours">
          <div className="rounded-xl border border-border bg-card">
            <div className="border-b border-border p-3">
              <h2 className="text-sm font-semibold">Patrol tour completion</h2>
            </div>
            <div className="divide-y divide-border">
              {filteredTours.length === 0 ? (
                <div className="p-6 text-center text-xs text-muted-foreground">No tour data yet.</div>
              ) : filteredTours.map((t, idx) => {
                const rate = t.total > 0 ? Math.round((t.completed / t.total) * 100) : 0;
                return (
                  <div key={idx} className="p-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium">{t.tour_name}</div>
                        <div className="text-xs text-muted-foreground">
                          {t.site_name ?? "—"} · last run {t.last_run_at ? new Date(t.last_run_at).toLocaleString() : "—"}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold">{rate}%</div>
                        <div className="text-[10px] text-muted-foreground">
                          {t.completed}/{t.total} completed{t.missed > 0 ? ` · ${t.missed} with issues` : ""}
                        </div>
                      </div>
                    </div>
                    <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
                      <div className="h-full bg-primary" style={{ width: `${rate}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="geofence">
          <div className="rounded-xl border border-border bg-card">
            <div className="border-b border-border p-3">
              <h2 className="text-sm font-semibold">Geofence breach alerts</h2>
            </div>
            <div className="divide-y divide-border">
              {geo.length === 0 ? (
                <div className="p-6 text-center text-xs text-muted-foreground">No breaches recorded.</div>
              ) : geo.map((g) => (
                <div key={g.id} className="flex items-center gap-3 p-3">
                  <ShieldAlert className={`h-4 w-4 ${g.event_type === "breach" ? "text-destructive" : "text-warning"}`} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium capitalize">Geofence {g.event_type}</div>
                    <div className="text-xs text-muted-foreground">
                      {new Date(g.occurred_at ?? g.created_at).toLocaleString()}
                    </div>
                  </div>
                  <Badge variant={g.event_type === "breach" ? "destructive" : "outline"}>{g.event_type}</Badge>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
